import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Search, Image as ImageIcon, Video, Music, Loader2, Check, Upload, FolderOpen, Filter, ImagePlus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPE_OPTIONS = [
  { value: "all", label: "Semua", icon: ImagePlus },
  { value: "image", label: "Gambar", icon: ImageIcon },
  { value: "video", label: "Video", icon: Video },
  { value: "audio", label: "Audio", icon: Music },
];

/**
 * MediaPickerModal — pick media URL from library or upload directly.
 * Props:
 *   open: bool
 *   onClose: () => void
 *   onSelect: (url: string, mediaRecord?: object) => void
 *   accept: "image" | "video" | "audio" | "all" (default 'all')
 *   title: string (optional, override modal title)
 */
export default function MediaPickerModal({ open, onClose, onSelect, accept = "all", title }) {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [folder, setFolder] = useState("all");
  const [type, setType] = useState(accept === "all" ? "all" : accept);
  const [selected, setSelected] = useState(null); // url
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelected(null);
      setSelectedRecord(null);
      setSearch("");
      setFolder("all");
      setType(accept === "all" ? "all" : accept);
    }
  }, [open, accept]);

  const fetchFolders = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/cms/media/folders`, { withCredentials: true });
      setFolders(data.folders || []);
    } catch (e) {
      // silent
    }
  };

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      if (folder && folder !== "all") params.set("folder", folder);
      if (type && type !== "all") params.set("type", type);
      params.set("sort", "created_desc");
      params.set("limit", "200");
      const { data } = await axios.get(`${API}/admin/cms/media?${params.toString()}`, { withCredentials: true });
      // Backend returns { items, count, total }. Old version returned 'media'. Use items first.
      setItems(data.items || data.media || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = setTimeout(fetchMedia, 200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, search, folder, type]);

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    let firstUploadedUrl = null;
    let firstRecord = null;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("folder", folder !== "all" ? folder : "uploads");
      try {
        const res = await axios.post(`${API}/admin/cms/media`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        });
        if (!firstUploadedUrl) {
          firstUploadedUrl = res.data.url;
          firstRecord = res.data;
        }
      } catch (e) {
        // continue
      }
    }
    setUploading(false);
    await fetchFolders();
    await fetchMedia();
    // Auto-select the first uploaded
    if (firstUploadedUrl) {
      setSelected(firstUploadedUrl);
      setSelectedRecord(firstRecord);
    }
  };

  const confirm = () => {
    if (selected) {
      onSelect(selected, selectedRecord);
      onClose();
    }
  };

  const absoluteUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

  if (!open) return null;

  const filteredCount = items.length;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-[#1C1A19]/55 backdrop-blur-sm"
        onClick={e => e.target === e.currentTarget && onClose()}
        data-testid="media-picker-modal"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white rounded-2xl shadow-[0_24px_64px_-12px_rgba(60,40,50,0.35)] w-full max-w-5xl max-h-[88vh] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#D4859B]/12 bg-gradient-to-b from-[#FDF8F6] to-white">
            <div>
              <h2 className="font-heading text-2xl text-[#1C1A19]">
                {title || (
                  <>Pilih dari <em className="text-seikat-pink">Media Library</em></>
                )}
              </h2>
              <p className="font-body text-xs text-[#8A857D] mt-0.5">Klik untuk pilih, atau upload langsung.</p>
            </div>
            <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-seikat-pink/10 flex items-center justify-center text-[#4A453F]">
              <X size={16} />
            </button>
          </div>

          {/* Toolbar */}
          <div className="px-6 py-3 border-b border-[#D4859B]/8 bg-white">
            <div className="flex items-center gap-3 flex-wrap">
              {/* Search */}
              <div className="flex items-center gap-2 bg-[#FDF8F6] border border-[#D4859B]/15 rounded-full px-4 py-2 flex-1 min-w-[200px] max-w-md">
                <Search size={13} className="text-[#A8A29A]" />
                <input
                  className="bg-transparent outline-none text-sm font-body text-[#1C1A19] placeholder:text-[#A8A29A]/70 flex-1"
                  placeholder="Cari nama, alt, tag…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="picker-search"
                />
              </div>
              {/* Folder */}
              <div className="flex items-center gap-2 bg-[#FDF8F6] border border-[#D4859B]/15 rounded-full px-3 py-2">
                <FolderOpen size={13} className="text-seikat-pink" />
                <select
                  className="bg-transparent outline-none text-sm font-body text-[#1C1A19] pr-2"
                  value={folder}
                  onChange={e => setFolder(e.target.value)}
                  data-testid="picker-folder"
                >
                  <option value="all">Semua Folder</option>
                  {folders.map(f => (
                    <option key={f.name} value={f.name}>{f.name} ({f.count})</option>
                  ))}
                </select>
              </div>
              {/* Type chips */}
              {accept === "all" && (
                <div className="flex items-center gap-1 bg-[#FDF8F6] border border-[#D4859B]/15 rounded-full p-1">
                  {TYPE_OPTIONS.map(opt => {
                    const active = type === opt.value;
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setType(opt.value)}
                        data-testid={`picker-type-${opt.value}`}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-body transition-colors ${
                          active ? "bg-seikat-pink text-white shadow-sm" : "text-[#4A453F] hover:bg-white"
                        }`}
                      >
                        <Icon size={11} />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {/* Upload button */}
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="picker-upload"
                className="flex items-center gap-1.5 px-4 py-2 bg-seikat-pink text-white rounded-full text-xs font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-50"
              >
                {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                {uploading ? "Mengupload…" : "Upload Baru"}
              </button>
              <input
                ref={fileRef}
                type="file"
                hidden
                multiple
                accept={accept === "image" ? "image/*" : accept === "video" ? "video/*" : accept === "audio" ? "audio/*" : "image/*,video/*,audio/*"}
                onChange={e => handleUpload(Array.from(e.target.files || []))}
              />
            </div>
          </div>

          {/* Items grid */}
          <div className="flex-1 overflow-y-auto bg-[#FDF8F6]/40 p-5">
            {loading ? (
              <div className="flex justify-center py-16"><Loader2 className="animate-spin text-seikat-pink" size={26} /></div>
            ) : filteredCount === 0 ? (
              <div className="text-center py-16">
                <ImageIcon size={34} className="mx-auto mb-3 text-[#D4859B]/35" />
                <p className="font-body text-sm text-[#4A453F]">Tidak ada media ditemukan</p>
                <p className="font-body text-xs text-[#A8A29A] mt-1">Coba ganti filter atau upload media baru di atas.</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {items.map(m => {
                  const isImage = m.mime?.startsWith("image/");
                  const isVideo = m.mime?.startsWith("video/");
                  const isAudio = m.mime?.startsWith("audio/");
                  const isSelected = selected === m.url;
                  return (
                    <button
                      key={m.media_id}
                      type="button"
                      onClick={() => { setSelected(isSelected ? null : m.url); setSelectedRecord(isSelected ? null : m); }}
                      data-testid={`picker-item-${m.media_id}`}
                      className={`relative rounded-xl overflow-hidden aspect-square border-2 transition-all bg-white shadow-sm ${
                        isSelected
                          ? "border-seikat-pink shadow-[0_0_0_3px_rgba(212,133,155,0.25)] scale-[0.98]"
                          : "border-transparent hover:border-seikat-pink/35 hover:shadow-md"
                      }`}
                    >
                      {isImage ? (
                        <img src={absoluteUrl(m.url)} alt={m.alt_text || m.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : isVideo ? (
                        <div className="w-full h-full bg-[#1C1A19]/8 flex flex-col items-center justify-center gap-1">
                          <Video size={22} className="text-seikat-pink/70" />
                          <span className="text-[9px] font-body text-[#4A453F] truncate px-2 max-w-full">{m.name}</span>
                        </div>
                      ) : isAudio ? (
                        <div className="w-full h-full bg-[#C5DB7B]/10 flex flex-col items-center justify-center gap-1">
                          <Music size={22} className="text-[#A8B85F]" />
                          <span className="text-[9px] font-body text-[#4A453F] truncate px-2 max-w-full">{m.name}</span>
                        </div>
                      ) : (
                        <div className="w-full h-full bg-[#1C1A19]/5 flex items-center justify-center">
                          <span className="text-[10px] font-body text-[#8A857D] uppercase tracking-wider">{(m.mime || "file").split("/")[1] || "file"}</span>
                        </div>
                      )}
                      {/* Tags badge */}
                      {Array.isArray(m.tags) && m.tags.length > 0 && (
                        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-black/45 backdrop-blur-sm text-white text-[8px] font-body">{m.tags[0]}{m.tags.length > 1 ? ` +${m.tags.length - 1}` : ""}</div>
                      )}
                      {isSelected && (
                        <div className="absolute inset-0 bg-seikat-pink/20 flex items-center justify-center">
                          <div className="w-8 h-8 rounded-full bg-seikat-pink flex items-center justify-center shadow-lg">
                            <Check size={14} className="text-white" />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-[#D4859B]/12 flex items-center justify-between gap-3 bg-white">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {selectedRecord ? (
                <>
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-[#FDF8F6] flex items-center justify-center flex-shrink-0">
                    {selectedRecord.mime?.startsWith("image/") ? (
                      <img src={absoluteUrl(selectedRecord.url)} alt="" className="w-full h-full object-cover" />
                    ) : selectedRecord.mime?.startsWith("video/") ? (
                      <Video size={14} className="text-seikat-pink" />
                    ) : (
                      <Music size={14} className="text-[#A8B85F]" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="font-body text-xs font-medium text-[#1C1A19] truncate">{selectedRecord.name}</p>
                    <p className="font-body text-[10px] text-[#8A857D]">{Math.round((selectedRecord.size || 0) / 1024)} KB · {selectedRecord.folder}</p>
                  </div>
                </>
              ) : (
                <p className="font-body text-xs text-[#8A857D]">{filteredCount} item{filteredCount !== 1 ? "s" : ""} · Pilih satu untuk lanjut.</p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-[#D4859B]/20 rounded-full text-sm font-body text-[#4A453F] hover:bg-[#FDF8F6] transition-colors"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={confirm}
                disabled={!selected}
                data-testid="media-picker-confirm"
                className="px-4 py-2 bg-seikat-pink text-white rounded-full text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {selected ? "Gunakan URL ini" : "Pilih dulu"}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
